import { ResponseArray } from '@/types/shared'
import { formatDate, formatDateWithFormatter } from '@/utils/function'
import { useInfiniteQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading, Typogaphy } from 'vnetwork-ui'
import Changelogs from './Changelogs'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import SearchIcons from '@/icons/SearchIcons'
import ChevronDownIcons from '@/icons/ChevronDownIcons'
import AddCircleIcons from '@/icons/AddCircleIcons'
import EditCirleIcons from '@/icons/EditCircleIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import EyeIcons from '@/icons/EyeIcons'
import SendIcons from '@/icons/SendIcons'
import CheckCircleIcons from '@/icons/CheckCircleIcons'
import CloseIcons from '@/icons/CloseIcons'
import InfoIcons from '@/icons/InfoIcons'

dayjs.extend(relativeTime)

export type ActivityType = {
    created_at: string
    employee_name: string
    action: string
    description: string
    metadata?: any
    task_id?: string
    status?: 'failed' | 'resolved' | 'unresolved'
    resolved_at?: string
    avatar?: string
}

export type ActivityPayload = {
    page: number
    page_size: number
    search?: string
}

interface ActivitiesProps {
    className?: string
    queryKey: string
    apiCall: (payload: ActivityPayload) => Promise<ResponseArray<ActivityType>>
    translationNamespace?: string
    onCreateTicket?: (activity: ActivityType) => void
    enableSearch?: boolean
}

const PAGE_SIZE = 10
const today = dayjs()

// Get action icon based on action type
const getActionIcon = (action: string) => {
    const iconClass = 'w-3.5 h-3.5'
    switch (action?.toLowerCase()) {
        case 'create':
        case 'created':
        case 'created_customer':
        case 'created-process':
        case 'items-created':
            return <AddCircleIcons className={`${iconClass} text-success`} />
        case 'update':
        case 'updated':
        case 'edit':
        case 'updated-process':
        case 'status-changed':
            return <EditCirleIcons className={`${iconClass} text-primary`} />
        case 'delete':
        case 'deleted':
        case 'deleted-process':
            return <DeleteOutlineIcons className={`${iconClass} text-error`} />
        case 'view':
        case 'viewed':
            return <EyeIcons className={`${iconClass} text-gray-500`} />
        case 'send':
        case 'sent':
        case 'send_notification':
            return <SendIcons className={`${iconClass} text-success`} />
        case 'approve':
        case 'approved':
        case 'confirm':
        case 'confirmed':
        case 'approved-process':
            return <CheckCircleIcons className={`${iconClass} text-success`} />
        case 'reject':
        case 'rejected':
        case 'cancel':
        case 'cancelled':
        case 'rejected-process':
        case 'items-creation-failed':
            return <CloseIcons className={`${iconClass} text-error`} />
        default:
            return <InfoIcons className={`${iconClass} text-gray-400`} />
    }
}

// Helper function to check if content contains HTML tags
const isHTMLContent = (content: string): boolean => {
    if (!content) return false
    const htmlRegex = /<\/?[a-z][\s\S]*>/i
    return htmlRegex.test(content)
}

// Get relative time or formatted time
const getTimeDisplay = (dateStr: string): string => {
    const date = dayjs(dateStr)
    const now = dayjs()
    const diffMinutes = now.diff(date, 'minute')

    if (diffMinutes < 60) {
        return `${diffMinutes} min ago`
    }
    return formatDateWithFormatter(dateStr, 'h:mm a')
}

// Get status badge style
const getStatusBadgeStyle = (status?: string) => {
    switch (status?.toLowerCase()) {
        case 'failed':
            return 'bg-red-50 text-red-600'
        case 'resolved':
            return 'bg-green-50 text-green-600'
        default:
            return 'bg-gray-50 text-gray-600'
    }
}

// Avatar component with fallback
const Avatar: React.FC<{ name?: string; src?: string }> = ({ name, src }) => {
    const initials = name
        ?.split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()

    if (src) {
        return (
            <img
                src={src}
                alt={name}
                className="w-6 h-6 rounded-full object-cover flex-shrink-0"
            />
        )
    }

    return (
        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[8px] font-medium text-gray-600 flex-shrink-0">
            {initials || '?'}
        </div>
    )
}

// Activity Card Component
const ActivityCard: React.FC<{
    activity: ActivityType
    translationNamespace: string
    onCreateTicket?: (activity: ActivityType) => void
}> = ({ activity, translationNamespace, onCreateTicket }) => {
    const { t } = useTranslation()

    const statusLabel = activity.status || 'unresolved'
    const isResolved = statusLabel === 'resolved'

    return (
        <div className="bg-white border border-gray-200 rounded-md px-4 py-2">
            {/* Header row */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex gap-2 flex-1 min-w-0">
                    <Avatar
                        name={activity.employee_name}
                        src={activity.avatar}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs text-gray-900">
                            {activity.employee_name}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-500 mt-0.5">
                            <span>
                                {t(`types.${activity?.action?.toLowerCase()}`, {
                                    ns: translationNamespace,
                                    defaultValue: activity?.action,
                                })}
                            </span>
                            {activity.task_id && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-gray-300 bg-white text-[10px] font-medium text-gray-700">
                                    Task ID #{activity.task_id}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {activity.status && (
                    <span
                        className={`px-2 py-0.5 rounded text-[10px] font-medium capitalize flex-shrink-0 ${getStatusBadgeStyle(
                            activity.status
                        )}`}
                    >
                        {t(activity.status)}
                    </span>
                )}
            </div>

            {/* Description */}
            <div className="mt-2 text-xs text-gray-600 ps-8">
                {isHTMLContent(activity?.description) ? (
                    <TextEditorCustomize
                        value={{
                            text: activity?.description,
                            attachments: [],
                        }}
                        readOnly={true}
                        className="border-none p-0 bg-transparent text-xs"
                    />
                ) : (
                    <span>
                        {t(`descriptions.${activity?.description}`, {
                            ns: translationNamespace,
                            defaultValue: activity?.description,
                        })}
                    </span>
                )}
            </div>

            {/* Metadata/Changelogs */}
            {!!activity?.metadata &&
                Object.keys(activity?.metadata ?? {}).length > 0 && (
                    <Changelogs metadata={activity?.metadata} />
                )}

            {/* Footer row - only show if resolved or has create ticket action */}
            {(isResolved || (!isResolved && onCreateTicket)) && (
                <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between ps-8">
                    {isResolved ? (
                        <div className="text-xs text-gray-500">
                            <span className="text-green-600 font-medium">
                                Resolved
                            </span>
                            {activity.resolved_at && (
                                <span className="ml-1">
                                    {formatDateWithFormatter(
                                        activity.resolved_at,
                                        'ddd, M/D/YY, h:mm:ss A'
                                    )}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div />
                    )}
                    {!isResolved && onCreateTicket && (
                        <button
                            onClick={() => onCreateTicket(activity)}
                            className="text-xs text-primary hover:text-primary/80 font-medium"
                        >
                            Create ticket
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

// Date Header Component
const DateHeader: React.FC<{
    dateLabel: string
    count?: number
    isCollapsed: boolean
    onToggle: () => void
}> = ({ dateLabel, count, isCollapsed, onToggle }) => {
    return (
        <div
            className="flex items-center gap-3 py-1.5 cursor-pointer bg-gray-50"
            onClick={onToggle}
        >
            <ChevronDownIcons
                className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isCollapsed ? '-rotate-90' : ''}`}
            />
            <div className="flex-1 flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-300" />
                <Typogaphy className="text-xs font-medium text-gray-600 whitespace-nowrap">
                    {dateLabel}
                </Typogaphy>
                <div className="flex-1 h-px bg-gray-300" />
            </div>
            {count !== undefined && (
                <span className="text-[10px] text-gray-500 font-medium flex-shrink-0 bg-gray-200 px-2 py-0.5 rounded-full">
                    {count}
                </span>
            )}
        </div>
    )
}

// Date Group Component
const DateGroup: React.FC<{
    date: string
    activities: ActivityType[]
    translationNamespace: string
    onCreateTicket?: (activity: ActivityType) => void
    count?: number
    isCollapsed: boolean
    onToggleCollapse: () => void
}> = ({
    date,
    activities,
    translationNamespace,
    onCreateTicket,
    count,
    isCollapsed,
    onToggleCollapse,
}) => {
    const { t } = useTranslation()

    const dateLabel =
        formatDateWithFormatter(date) === formatDate(today.format())
            ? t('today')
            : formatDateWithFormatter(
                  activities[0]?.created_at,
                  'ddd, DD MMM YYYY'
              )

    return (
        <div>
            <DateHeader
                dateLabel={dateLabel}
                count={count}
                isCollapsed={isCollapsed}
                onToggle={onToggleCollapse}
            />

            {/* Activities Timeline */}
            {!isCollapsed && (
                <div className="ml-2">
                    {activities?.length > 0 &&
                        activities?.map((activity, index) => (
                            <div key={index} className="flex gap-3">
                                {/* Time */}
                                <div className="w-12 flex-shrink-0">
                                    <span className="text-xs text-gray-500 whitespace-nowrap uppercase">
                                        {getTimeDisplay(activity.created_at)}
                                    </span>
                                </div>

                                {/* Timeline */}
                                <div className="flex flex-col items-center">
                                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                                        {getActionIcon(activity?.action)}
                                    </div>
                                    {index < activities.length - 1 && (
                                        <div className="w-0.5 bg-gray-200 flex-1 mt-1" />
                                    )}
                                </div>

                                {/* Card */}
                                <div className="flex-1 pb-3">
                                    <ActivityCard
                                        activity={activity}
                                        translationNamespace={
                                            translationNamespace
                                        }
                                        onCreateTicket={onCreateTicket}
                                    />
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    )
}

const Activities: React.FC<ActivitiesProps> = ({
    apiCall,
    queryKey,
    className,
    translationNamespace = 'customer',
    onCreateTicket,
    enableSearch = false,
}) => {
    const { t } = useTranslation()
    const loadMoreRef = useRef<HTMLDivElement | null>(null)
    const [searchValue, setSearchValue] = useState('')
    const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(
        new Set()
    )

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
        useInfiniteQuery({
            queryKey: [
                QUERY_KEYS.ACTIVITIES,
                queryKey,
                ...(enableSearch ? [searchValue] : []),
            ],
            queryFn: ({ pageParam = 1 }) =>
                apiCall({
                    page: pageParam,
                    page_size: PAGE_SIZE,
                    ...(enableSearch && searchValue
                        ? { search: searchValue }
                        : {}),
                }),
            getNextPageParam: (lastPage, _, lastPageParam) => {
                const total = lastPage?.pagination?.total ?? 0
                const loaded = lastPageParam * PAGE_SIZE
                return loaded < total ? lastPageParam + 1 : undefined
            },
            initialPageParam: 1,
        })

    const allActivities = useMemo(() => {
        return (data?.pages ?? []).flatMap((p) => p.results ?? [])
    }, [data])

    const grouped = useMemo(() => {
        return allActivities.reduce<Record<string, ActivityType[]>>(
            (acc, cur) => {
                const date = formatDate(cur.created_at)
                if (!acc[date]) acc[date] = []
                acc[date].push(cur)
                return acc
            },
            {}
        )
    }, [allActivities])

    const groupedEntries = useMemo(() => Object.entries(grouped), [grouped])

    // Get count per date group
    const getGroupCount = (date: string) => {
        return grouped[date]?.length ?? 0
    }

    // Toggle collapse for a group
    const toggleCollapse = useCallback((index: number) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(index)) {
                next.delete(index)
            } else {
                next.add(index)
            }
            return next
        })
    }, [])

    useEffect(() => {
        if (!loadMoreRef.current || !hasNextPage) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    fetchNextPage()
                }
            },
            {
                threshold: 1.0,
            }
        )

        observer.observe(loadMoreRef.current)

        return () => observer.disconnect()
    }, [fetchNextPage, hasNextPage])

    return (
        <div
            className={`rounded-md bg-gray-50 px-4 py-2 max-h-[600px] overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 ${className}`}
            style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#d1d5db transparent',
            }}
        >
            {/* Search Header */}
            {enableSearch && (
                <div className="mb-6">
                    <div className="relative">
                        <SearchIcons className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('search-activities-placeholder', {
                                defaultValue:
                                    'Search with Brand, Site, Task ID, Error Message, etc',
                            })}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {/* Activities grouped by date */}
            {groupedEntries?.map(([date, acts], index) => (
                <DateGroup
                    key={date}
                    date={date}
                    activities={acts}
                    translationNamespace={translationNamespace}
                    onCreateTicket={onCreateTicket}
                    count={getGroupCount(date)}
                    isCollapsed={collapsedGroups.has(index)}
                    onToggleCollapse={() => toggleCollapse(index)}
                />
            ))}

            {/* Loading indicator */}
            {isFetchingNextPage && (
                <div className="flex items-center justify-center py-4">
                    <Loading.Ring width={20} height={20} />
                </div>
            )}

            {/* Load more trigger */}
            {hasNextPage && <div ref={loadMoreRef} className="h-6 w-full" />}

            {/* Empty state */}
            {allActivities.length === 0 && !isFetchingNextPage && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Typogaphy>{t('no-activities')}</Typogaphy>
                </div>
            )}
        </div>
    )
}

export default Activities

// Skeleton Components
const ActivityItemSkeleton = () => (
    <div className="flex gap-4 animate-pulse">
        <div className="w-20 pt-1">
            <div className="h-3 w-14 bg-gray-300 rounded" />
        </div>
        <div className="flex flex-col items-center">
            <div className="w-2.5 h-2.5 rounded-full bg-gray-300 mt-1" />
            <div className="w-0.5 bg-gray-200 flex-1 my-1" />
        </div>
        <div className="flex-1 pb-3">
            <div className="bg-white border border-gray-200 rounded-md p-3">
                <div className="flex gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-gray-300 flex-shrink-0" />
                    <div className="flex-1">
                        <div className="h-3 w-24 bg-gray-300 rounded mb-1" />
                        <div className="h-2.5 w-32 bg-gray-300 rounded" />
                    </div>
                </div>
                <div className="h-2.5 w-full bg-gray-300 rounded mb-1.5" />
                <div className="h-2.5 w-3/4 bg-gray-300 rounded" />
            </div>
        </div>
    </div>
)

const DateGroupSkeleton = () => (
    <div className="mb-6 animate-pulse">
        <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-gray-300 rounded" />
                <div className="h-5 w-32 bg-gray-300 rounded" />
            </div>
            <div className="h-4 w-8 bg-gray-300 rounded" />
        </div>
        <div className="ml-2">
            <ActivityItemSkeleton />
            <ActivityItemSkeleton />
            <ActivityItemSkeleton />
        </div>
    </div>
)

export const ActivitiesSkeleton: React.FC<{ enableSearch?: boolean }> = ({
    enableSearch = false,
}) => {
    return (
        <div
            className="rounded-md bg-gray-50 p-4 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#d1d5db transparent',
            }}
        >
            {/* Search skeleton */}
            {enableSearch && (
                <div className="mb-6 animate-pulse">
                    <div className="h-10 bg-gray-300 rounded-md" />
                </div>
            )}

            <DateGroupSkeleton />
            <DateGroupSkeleton />
        </div>
    )
}
